import { db } from "../App";
import { ScheduleDocument, ScheduleItem, SchedulePosition } from "../interfaces/ISchedules";
import {
  DocumentData,
  QueryDocumentSnapshot,
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

type ScheduleData = {
  id?: string;
  contestId?: string;
  day?: number;
  dayLabel?: string;
  date?: string;
  isPublished?: boolean;
  positions?: Array<{
    id?: string;
    plannedStartTime?: unknown;
    hideStartTime?: boolean;
    liveState?: SchedulePosition["liveState"];
    items?: Array<{
      id?: string;
      type?: string;
      competitionRoundId?: string;
      competitionCategoryId?: string;
      floorLabel?: string;
      title?: string;
      subtitle?: string;
      stats?: ScheduleItem["stats"];
    }>;
  }>;
  updatedAt?: ScheduleDocument["updatedAt"];
};

const schedulesCollection = (competitionId: string) => collection(db, "contests", competitionId, "schedules");

/** Indeks złożony `isPublished` + `day` (COLLECTION) — bez `orderBy` Firestore żądał indeksu odrzucanego przy deployu („single field index controls”). */
const publishedSchedulePresenceQuery = (contestId: string) =>
  query(
    schedulesCollection(contestId),
    where("isPublished", "==", true),
    orderBy("day", "asc"),
    limit(1),
  );

/** Ścieżka `contests/{id}/schedules` już określa turniej — nie filtrujemy po polu `contestId` (może być puste lub nieaktualne). */
const buildSchedulesQuery = (competitionId: string) =>
  query(
    schedulesCollection(competitionId),
    where("isPublished", "==", true),
    orderBy("day", "asc"),
  );

const mapScheduleItem = (
  item: NonNullable<NonNullable<ScheduleData["positions"]>[number]["items"]>[number],
  index: number,
): ScheduleItem => ({
  id: item.id || `item-${index}`,
  type: item.type || "UNKNOWN",
  competitionRoundId: item.competitionRoundId,
  competitionCategoryId: item.competitionCategoryId,
  floorLabel: item.floorLabel,
  title: item.title,
  subtitle: item.subtitle,
  stats: item.stats,
});

const mapSchedulePosition = (
  position: NonNullable<ScheduleData["positions"]>[number],
  index: number,
): SchedulePosition => ({
  id: position.id || `position-${index}`,
  plannedStartTime: position.plannedStartTime,
  hideStartTime: position.hideStartTime === true,
  liveState: position.liveState,
  items: (position.items || []).map(mapScheduleItem),
});

const mapScheduleDocument = (
  scheduleSnapshot: QueryDocumentSnapshot<DocumentData>,
  competitionId: string,
): ScheduleDocument => {
  const scheduleData = scheduleSnapshot.data() as ScheduleData;

  return {
    id: scheduleData.id || scheduleSnapshot.id,
    contestId: scheduleData.contestId || competitionId,
    day: Number(scheduleData.day || 0),
    dayLabel: scheduleData.dayLabel || "",
    date: scheduleData.date,
    isPublished: scheduleData.isPublished === true,
    positions: (scheduleData.positions || []).map(mapSchedulePosition),
    updatedAt: scheduleData.updatedAt,
  };
};

const filterPublishedSchedules = (schedules: ScheduleDocument[]) =>
  schedules.filter((schedule) => schedule.isPublished === true);

export const findPublishedSchedules = async (competitionId: string): Promise<ScheduleDocument[]> => {
  const snapshot = await getDocs(buildSchedulesQuery(competitionId));
  const schedules = snapshot.docs.map((scheduleSnapshot) => mapScheduleDocument(scheduleSnapshot, competitionId));

  return filterPublishedSchedules(schedules);
};

export const subscribePublishedSchedules = (
  competitionId: string,
  onData: (schedules: ScheduleDocument[]) => void,
  onError: (error: Error) => void,
) =>
  onSnapshot(
    buildSchedulesQuery(competitionId),
    (snapshot) => {
      const schedules = snapshot.docs.map((scheduleSnapshot) => mapScheduleDocument(scheduleSnapshot, competitionId));
      onData(filterPublishedSchedules(schedules));
    },
    (error) => {
      onError(error);
    },
  );

/**
 * Lista turniejów z opublikowanym harmonogramem — zapytania pod `contests/{id}/schedules`.
 *
 * Nie używamy `collectionGroup("schedules")`: w projekcie istnieje też root `schedules/`
 * (legacy Skrutiner). Dla niezalogowanego użytkownika reguły na tej ścieżce wymagają `auth`,
 * więc collection group z `isPublished` jest niewiarygodne / puste w aplikacji `results`.
 */
export const findContestIdsWithPublishedSchedules = async (
  contestIds: readonly string[],
): Promise<Set<string>> => {
  const ids = new Set<string>();
  const unique = Array.from(new Set(contestIds.filter((id) => id && id.length > 0)));
  await Promise.all(
    unique.map(async (contestId) => {
      const snapshot = await getDocs(publishedSchedulePresenceQuery(contestId));
      if (!snapshot.empty) {
        ids.add(contestId);
      }
    }),
  );
  return ids;
};

export const subscribeContestIdsWithPublishedSchedules = (
  contestIds: readonly string[],
  onData: (contestIds: Set<string>) => void,
  onError: (error: Error) => void,
): (() => void) => {
  const unsubByContestId = new Map<string, () => void>();
  const withPublished = new Set<string>();
  let disposed = false;

  const emit = () => {
    if (!disposed) {
      onData(new Set(withPublished));
    }
  };

  const sync = (ids: readonly string[]) => {
    const want = new Set(ids.filter((id) => id && id.length > 0));

    const staleIds: string[] = [];
    unsubByContestId.forEach((unsub, cid) => {
      if (!want.has(cid)) {
        unsub();
        staleIds.push(cid);
        withPublished.delete(cid);
      }
    });
    staleIds.forEach((id) => {
      unsubByContestId.delete(id);
    });

    want.forEach((cid) => {
      if (unsubByContestId.has(cid)) {
        return;
      }
      const publishedQuery = publishedSchedulePresenceQuery(cid);
      const unsub = onSnapshot(
        publishedQuery,
        (snap) => {
          if (disposed) {
            return;
          }
          if (snap.empty) {
            withPublished.delete(cid);
          } else {
            withPublished.add(cid);
          }
          emit();
        },
        (err) => {
          if (!disposed) {
            onError(err instanceof Error ? err : new Error(String(err)));
          }
        },
      );
      unsubByContestId.set(cid, unsub);
    });

    emit();
  };

  sync(contestIds);

  return () => {
    disposed = true;
    unsubByContestId.forEach((u) => {
      u();
    });
    unsubByContestId.clear();
    withPublished.clear();
  };
};
